import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useOrderGroup } from "@/api/hooks/use-orders";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import {
  OrderDetailHeaderSection,
  OrderStatusSections,
  OrderDetailAddressPaymentSection,
  ProductRecommendationsSection,
} from "@/features/orders";

const OrderDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { orderGroupId } = useParams<{ orderGroupId: string }>();
  const navigate = useNavigate();

  // Fetch order group data
  const {
    data: orderGroupResponse,
    isLoading,
    isError,
    error,
  } = useOrderGroup(orderGroupId || null);

  // Extract orders and detail from response
  const orders = orderGroupResponse?.data?.orders || [];
  const orderGroupDetail = orderGroupResponse?.data?.detail;

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto bg-bg-primary dark:bg-bg-primary">
        <div className="max-w-6xl mx-auto py-8 px-4 md:px-8">
          <div className="flex justify-center items-center py-16">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !orderGroupId || orders.length === 0) {
    return (
      <div className="h-full overflow-y-auto bg-bg-primary dark:bg-bg-primary">
        <div className="max-w-6xl mx-auto py-8 px-4 md:px-8">
          <div className="text-center py-16">
            <h1 className="text-2xl font-semibold text-text-primary dark:text-text-primary mb-2">
              {t("orders.errorTitle")}
            </h1>
            <p className="text-text-tertiary dark:text-text-tertiary mb-6">
              {error?.message || t("orders.errorMessage")}
            </p>
            <Button asChild variant="darkPrimary">
              <Link to="/">{t("orders.backToHome")}</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Get order group ID for header display
  const orderGroupIdDisplay =
    orderGroupDetail?.orderGroupId || orders[0]?.orderGroupId || orderGroupId;

  // Get total amount from detail (preferred) or calculate from orders
  const totalAmount = orderGroupDetail?.totalAmount || 0;
  const currency = orderGroupDetail?.currency || orders[0]?.currency || "TRY";

  return (
    <div className="h-full overflow-y-auto bg-bg-primary dark:bg-gray-900">
      {/* Main Content */}
      <div className="px-4 md:px-12 py-4 md:py-6">
        <div className="max-w-screen-2xl mx-auto w-full space-y-4 md:space-y-6">
          {/* Header Section */}
          <div>
            {/* Back Arrow + Title */}
            <div className="flex items-center gap-2 md:gap-[10px] mb-4 md:mb-8">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center justify-center w-5 h-5 md:w-6 md:h-6 text-text-primary dark:text-text-primary hover:text-text-secondary dark:hover:text-text-secondary transition-colors"
              >
                <ArrowLeft className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              <h1 className="text-lg md:text-2xl font-semibold text-text-primary dark:text-text-primary">
                {t("orders.detail.title")}
              </h1>
            </div>

            {/* Order Detail Header Section */}
            <OrderDetailHeaderSection
              orderGroupId={orderGroupIdDisplay}
              orders={orders}
              orderGroupDetail={orderGroupDetail}
              totalAmount={totalAmount}
              currency={currency}
            />
          </div>

          {/* Order Status Sections */}
          <OrderStatusSections orders={orders} />

          {/* Address & Payment Section */}
          <OrderDetailAddressPaymentSection
            orderGroupDetail={orderGroupDetail}
            currency={currency}
          />

          {/* Product Recommendations Section */}
          <ProductRecommendationsSection />
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
